import { buildApiUrl } from '../config/environment';

const getToken = () =>
  sessionStorage.getItem('token') ||
  sessionStorage.getItem('access_token') ||
  localStorage.getItem('token') ||
  localStorage.getItem('access_token');

/**
 * Fetch all active images for a property.
 * Optionally filter by imageType ('profile', 'gallery', etc.).
 */
export const getPropertyImages = async (agencyId, propertyId, imageType = null) => {
  const token = getToken();
  if (!token) throw new Error('Authentication token not found');

  let url = buildApiUrl(`/agencies/${agencyId}/properties/${propertyId}/images`);
  if (imageType) url += `?image_type=${encodeURIComponent(imageType)}`;

  const response = await fetch(url, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to fetch property images');
  }

  return await response.json(); // array of PropertyImageResponse
};

/**
 * Upload one or more image files to a property.
 * All files in a single call must share the same imageType.
 * @param {File[]} files
 * @param {string} imageType  e.g. 'gallery'
 * @param {Function} [onProgress]  optional (not natively supported by fetch; reserved)
 */
export const uploadPropertyImages = async (agencyId, propertyId, files, imageType = 'gallery') => {
  const token = getToken();
  if (!token) throw new Error('Authentication token not found');

  const formData = new FormData();
  files.forEach((file) => formData.append('files', file));
  formData.append('image_type', imageType);
  formData.append('access_level', 'public');

  const response = await fetch(
    buildApiUrl(`/agencies/${agencyId}/properties/${propertyId}/images/upload`),
    {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` }, // no Content-Type — browser sets boundary
      body: formData,
    }
  );

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to upload images');
  }

  return await response.json(); // { count, images, warnings? }
};

/**
 * Soft-delete a property image (sets is_active=false on the backend).
 */
export const deletePropertyImage = async (agencyId, propertyId, imageId) => {
  const token = getToken();
  if (!token) throw new Error('Authentication token not found');

  const response = await fetch(
    buildApiUrl(`/agencies/${agencyId}/properties/${propertyId}/images/${imageId}`),
    {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` },
    }
  );

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to delete image');
  }
  // 204 No Content — nothing to return
};

/**
 * Promote a profile-type image to be the default/cover image.
 * Only images with image_type='profile' can be set as default on the backend.
 */
export const setAsDefaultImage = async (agencyId, propertyId, imageId) => {
  const token = getToken();
  if (!token) throw new Error('Authentication token not found');

  const response = await fetch(
    buildApiUrl(`/agencies/${agencyId}/properties/${propertyId}/images/${imageId}/set-profile`),
    {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to set default image');
  }

  return await response.json();
};

/**
 * Persist a new sort order for all images.
 * @param {Object} imageOrders  e.g. { "5": 0, "3": 1, "7": 2 }  (string keys, integer values)
 */
export const reorderPropertyImages = async (agencyId, propertyId, imageOrders) => {
  const token = getToken();
  if (!token) throw new Error('Authentication token not found');

  const response = await fetch(
    buildApiUrl(`/agencies/${agencyId}/properties/${propertyId}/images/reorder`),
    {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ image_orders: imageOrders }),
    }
  );

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to reorder images');
  }

  return await response.json();
};

